import React, { useState, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@material-ui/core'
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto'
import VideocamIcon from '@material-ui/icons/Videocam'
import DeleteIcon from '@material-ui/icons/Delete'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import CloseIcon from '@material-ui/icons/Close'
import { CLOUDINARY_CLOUDNAME, CLOUDINARY_UPLOAD_PRESET_LARGE } from 'env'
import useStyles from './styles'
import VideoPlayer from '../VideoPlayer'

const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUDNAME}/auto/upload`
const CLOUDINARY_FOLDER = 'hole-in-one'

const MAX_FILE_SIZE_MB = 100
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]
const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/mov',
]
const ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES]

const isVideoFile = file => {
  if (file?.type) {
    return ACCEPTED_VIDEO_TYPES.includes(file.type)
  }
  return false
}

const MediaUpload = ({
  photos = [],
  videos = [],
  onPhotosChange,
  onVideosChange,
  disabled = false,
  maxPhotos = 10,
  maxVideos = 3,
}) => {
  const classes = useStyles()
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [previewVideo, setPreviewVideo] = useState(null)
  const [previewImage, setPreviewImage] = useState(null)

  const uploadToCloudinary = useCallback(async file => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET_LARGE)
    formData.append('folder', CLOUDINARY_FOLDER)

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', () => {})

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText)
          resolve({
            url: response.secure_url,
            publicId: response.public_id,
            resourceType: response.resource_type,
            format: response.format,
            width: response.width,
            height: response.height,
            duration: response.duration,
          })
        } else {
          reject(new Error('Upload failed'))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'))
      })

      xhr.open('POST', CLOUDINARY_UPLOAD_URL)
      xhr.send(formData)
    })
  }, [])

  const handleFileSelect = useCallback(
    async event => {
      const files = Array.from(event.target.files)
      if (!files.length) return

      setError(null)
      setUploading(true)

      const imageFiles = []
      const videoFiles = []

      for (const file of files) {
        if (!ACCEPTED_TYPES.includes(file.type)) {
          setError(`Unsupported file type: ${file.name}`)
          continue
        }

        const sizeMB = file.size / (1024 * 1024)
        if (sizeMB > MAX_FILE_SIZE_MB) {
          setError(`File too large: ${file.name} (max ${MAX_FILE_SIZE_MB}MB)`)
          continue
        }

        if (isVideoFile(file)) {
          if (videos.length + videoFiles.length < maxVideos) {
            videoFiles.push(file)
          } else {
            setError(`Maximum ${maxVideos} videos allowed`)
          }
        } else {
          if (photos.length + imageFiles.length < maxPhotos) {
            imageFiles.push(file)
          } else {
            setError(`Maximum ${maxPhotos} photos allowed`)
          }
        }
      }

      try {
        for (const file of imageFiles) {
          const result = await uploadToCloudinary(file)
          onPhotosChange([
            ...photos,
            {
              id: Date.now() + Math.random(),
              url: result.url,
              publicId: result.publicId,
              isNew: true,
            },
          ])
        }

        for (const file of videoFiles) {
          const result = await uploadToCloudinary(file)
          onVideosChange([
            ...videos,
            {
              id: Date.now() + Math.random(),
              url: result.url,
              publicId: result.publicId,
              duration: result.duration,
              isNew: true,
            },
          ])
        }
      } catch (err) {
        setError('Failed to upload file. Please try again.')
        console.error('Upload error:', err)
      } finally {
        setUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [
      photos,
      videos,
      maxPhotos,
      maxVideos,
      onPhotosChange,
      onVideosChange,
      uploadToCloudinary,
    ],
  )

  const handleRemovePhoto = useCallback(
    photoId => {
      onPhotosChange(photos.filter(p => p.id !== photoId))
    },
    [photos, onPhotosChange],
  )

  const handleRemoveVideo = useCallback(
    videoId => {
      onVideosChange(videos.filter(v => v.id !== videoId))
    },
    [videos, onVideosChange],
  )

  const handleOpenFileDialog = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [disabled])

  const getVideoThumbnail = url => {
    if (url.includes('cloudinary.com')) {
      return url
        .replace('/video/upload/', '/video/upload/so_0,w_200,h_200,c_fill/')
        .replace(/\.[^/.]+$/, '.jpg')
    }
    return null
  }

  return (
    <Box className={classes.root}>
      <Typography className={classes.sectionTitle}>Photos & Videos</Typography>
      <Typography className={classes.sectionDescription}>
        Upload photos and videos of your hole-in-one moment. Share the
        celebration!
      </Typography>

      {error && <Typography className={classes.errorText}>{error}</Typography>}

      <Box className={classes.mediaGrid}>
        {photos.map(photo => (
          <Box key={photo.id} className={classes.mediaItem}>
            <img
              src={photo.url}
              alt="Hole in one moment"
              className={classes.mediaImage}
              onClick={() => setPreviewImage(photo.url)}
            />
            {!disabled && (
              <IconButton
                className={classes.removeButton}
                onClick={() => handleRemovePhoto(photo.id)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}

        {videos.map(video => (
          <Box key={video.id} className={classes.mediaItem}>
            <Box className={classes.videoThumbnail}>
              {getVideoThumbnail(video.url) ? (
                <img
                  src={getVideoThumbnail(video.url)}
                  alt="Video thumbnail"
                  className={classes.mediaImage}
                />
              ) : (
                <Box className={classes.videoPlaceholder}>
                  <VideocamIcon className={classes.videoPlaceholderIcon} />
                </Box>
              )}
              <Box
                className={classes.playOverlay}
                onClick={() => setPreviewVideo(video.url)}
              >
                <PlayArrowIcon className={classes.playIcon} />
              </Box>
            </Box>
            {!disabled && (
              <IconButton
                className={classes.removeButton}
                onClick={() => handleRemoveVideo(video.id)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}

        {!disabled && (photos.length < maxPhotos || videos.length < maxVideos) && (
          <Box
            className={classes.addMediaButton}
            onClick={handleOpenFileDialog}
          >
            {uploading ? (
              <CircularProgress size={24} className={classes.uploadProgress} />
            ) : (
              <>
                <Box className={classes.addMediaIcons}>
                  <AddAPhotoIcon className={classes.addMediaIcon} />
                  <VideocamIcon className={classes.addMediaIcon} />
                </Box>
                <Typography className={classes.addMediaText}>
                  ADD MEDIA
                </Typography>
                <Typography className={classes.addMediaHint}>
                  Photos & Videos
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      <Typography className={classes.mediaLimits}>
        {photos.length}/{maxPhotos} photos • {videos.length}/{maxVideos} videos
      </Typography>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={ACCEPTED_TYPES.join(',')}
        multiple
        onChange={handleFileSelect}
        disabled={disabled || uploading}
      />

      {/* Video Preview Dialog */}
      <Dialog
        open={!!previewVideo}
        onClose={() => setPreviewVideo(null)}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.videoDialog }}
      >
        <IconButton
          className={classes.closeDialogButton}
          onClick={() => setPreviewVideo(null)}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent className={classes.videoDialogContent}>
          {previewVideo && (
            <VideoPlayer
              src={previewVideo}
              onClose={() => setPreviewVideo(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        classes={{ paper: classes.imageDialog }}
      >
        <IconButton
          className={classes.closeDialogButton}
          onClick={() => setPreviewImage(null)}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent className={classes.imageDialogContent}>
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              className={classes.previewImage}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default MediaUpload
